import api from './api';
import type {
  Transaction,
  TransactionDetail,
  TransactionFilters,
  TransactionReceipt,
  PaginatedResponse,
} from '../types';

class TransactionsService {
  /**
   * Get transactions with optional filters
   */
  async getTransactions(filters?: TransactionFilters & { sispStatus?: string }): Promise<Transaction[]> {
    const queryParams = new URLSearchParams();

    if (filters?.startDate) queryParams.append('startDate', filters.startDate);
    if (filters?.endDate) queryParams.append('endDate', filters.endDate);
    if (filters?.limit) queryParams.append('limit', filters.limit.toString());
    if (filters?.offset) queryParams.append('offset', filters.offset.toString());
    if (filters?.estado) queryParams.append('estado', filters.estado);
    if (filters?.entidadeId) queryParams.append('entidadeId', filters.entidadeId);
    // Filtro por estado SISP no servidor (N=Pago, F=Falhado, S=Pendente)
    if (filters?.sispStatus) queryParams.append('sispStatus', filters.sispStatus);

    const query = queryParams.toString();
    const url = `/client/transactions${query ? `?${query}` : ''}`;

    const response = await api.get<{
      success: boolean;
      data?: Transaction[];
      error?: string;
    }>(url);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar transações');
  }

  /**
   * Get paginated transactions
   */
  async getTransactionsPaginated(
    page: number = 1,
    limit: number = 20,
    filters?: Omit<TransactionFilters, 'limit' | 'offset'>
  ): Promise<PaginatedResponse<Transaction>> {
    const offset = (page - 1) * limit;
    const transactions = await this.getTransactions({
      ...filters,
      limit,
      offset,
    });

    // Note: The API should return total count for proper pagination
    return {
      items: transactions,
      total: transactions.length,
      page,
      limit,
      totalPages: Math.ceil(transactions.length / limit),
    };
  }

  /**
   * Get filtered transactions by date range
   */
  async getTransactionsByDateRange(
    startDate: string,
    endDate: string
  ): Promise<Transaction[]> {
    return this.getTransactions({ startDate, endDate });
  }

  /**
   * Get transaction details
   */
  async getTransactionDetails(transactionId: string): Promise<TransactionDetail> {
    const response = await api.get<{
      success: boolean;
      data?: TransactionDetail;
      error?: string;
    }>(`/client/transactions/${transactionId}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar detalhes da transação');
  }

  /**
   * Get transaction receipt
   */
  async getReceipt(transactionId: string): Promise<TransactionReceipt> {
    const response = await api.get<{
      success: boolean;
      data?: TransactionReceipt;
      error?: string;
    }>(`/client/transactions/${transactionId}/receipt`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar recibo');
  }

  /**
   * Get recent transactions
   */
  async getRecentTransactions(limit: number = 10): Promise<Transaction[]> {
    return this.getTransactions({ limit });
  }

  /**
   * Get filtered transactions by date range with limit
   */
  async getFilteredTransactions(
    startDate: string,
    endDate: string,
    limit: number = 10
  ): Promise<Transaction[]> {
    return this.getTransactions({ startDate, endDate, limit });
  }
}

export default new TransactionsService();
